import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const PlanFeaturesTab = () => {
  const [features, setFeatures] = useState([
    {
      id: 1,
      name: 'BMC Access',
      free: false,
      growth: true,
      scale: true,
      enterprise: true
    },
    {
      id: 2,
      name: 'Predictive Analysis',
      free: false,
      growth: true,
      scale: true,
      enterprise: true
    },
    {
      id: 3,
      name: 'Goal Intelligence',
      free: false,
      growth: false,
      scale: true,
      enterprise: true
    },
    {
      id: 4,
      name: 'Reports Download',
      free: false,
      growth: true,
      scale: true,
      enterprise: true
    },
    {
      id: 5,
      name: 'AI Suggestions Limit',
      free: '10/month',
      growth: '100/month',
      scale: '500/month',
      enterprise: 'Unlimited'
    },
    {
      id: 6,
      name: 'Team Members',
      free: '1',
      growth: '5',
      scale: '20',
      enterprise: 'Unlimited'
    },
    {
      id: 7,
      name: 'Priority Support',
      free: false,
      growth: false,
      scale: true,
      enterprise: true
    }
  ]);

  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleToggleFeature = (featureId, plan) => {
    if (!editMode) return;

    setFeatures(features?.map(feature => {
      if (feature?.id === featureId) {
        return { ...feature, [plan]: !feature?.[plan] };
      }
      return feature;
    }));
  };

  const handleTextChange = (featureId, plan, value) => {
    if (!editMode) return;

    setFeatures(features?.map(feature => {
      if (feature?.id === featureId) {
        return { ...feature, [plan]: value };
      }
      return feature;
    }));
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      console.log('Features saved:', features);
      setIsSaving(false);
      setEditMode(false);
    }, 1000);
  };

  const renderCell = (feature, plan) => {
    const value = feature?.[plan];

    if (typeof value === 'boolean') {
      return (
        <div className="flex items-center justify-center">
          {editMode ? (
            <button
              onClick={() => handleToggleFeature(feature?.id, plan)}
              className="p-2 hover:bg-muted rounded-md transition-smooth"
            >
              <Icon
                name={value ? 'CheckCircle' : 'XCircle'}
                size={20}
                className={value ? 'text-success' : 'text-muted-foreground'}
              />
            </button>
          ) : (
            <Icon
              name={value ? 'CheckCircle' : 'XCircle'}
              size={20}
              className={value ? 'text-success' : 'text-muted-foreground'}
            />
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center">
        {editMode ? (
          <input
            type="text"
            value={value}
            onChange={(e) => handleTextChange(feature?.id, plan, e?.target?.value)}
            className="w-full px-2 py-1 text-center bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        ) : (
          <span className="text-sm font-medium text-foreground">{value}</span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Grid" size={20} className="text-primary" />
              Feature Comparison Matrix
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {editMode ? 'Click cells to edit feature availability' : 'View feature availability across plans'}
            </p>
          </div>
          <div className="flex gap-2">
            {editMode ? (
              <>
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => setEditMode(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="default"
                  onClick={handleSave}
                  loading={isSaving}
                  iconName="Save"
                  iconPosition="left"
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                size="default"
                onClick={() => setEditMode(true)}
                iconName="Edit"
                iconPosition="left"
              >
                Edit Features
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Feature</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Free</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Growth</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Scale</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Enterprise</th>
            </tr>
          </thead>
          <tbody>
            {features?.map((feature) => (
              <tr key={feature?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-4 font-medium text-foreground">{feature?.name}</td>
                <td className="px-4 py-4">{renderCell(feature, 'free')}</td>
                <td className="px-4 py-4">{renderCell(feature, 'growth')}</td>
                <td className="px-4 py-4">{renderCell(feature, 'scale')}</td>
                <td className="px-4 py-4">{renderCell(feature, 'enterprise')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {features?.map((feature) => (
          <div key={feature?.id} className="bg-card border border-border rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-3">{feature?.name}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Free</p>
                {renderCell(feature, 'free')}
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Growth</p>
                {renderCell(feature, 'growth')}
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Scale</p>
                {renderCell(feature, 'scale')}
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Enterprise</p>
                {renderCell(feature, 'enterprise')}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanFeaturesTab;